import React, { useState, useEffect } from 'react';
import { Modal, Card, Button, Form, Alert, Table, Badge, Spinner } from 'react-bootstrap';
import { FaExclamationTriangle, FaCheckCircle, FaTimes, FaUsers, FaBuilding, FaClock } from 'react-icons/fa';
import apiClient from '../services/apiClient';

/**
 * MigrationSelectionModal Component
 * 
 * Handles user selection of connections during account downgrades.
 * Provides intelligent recommendations and clear impact visualization.
 * 
 * Props:
 * - show: Boolean to control modal visibility
 * - onHide: Function to close modal
 * - migrationAnalysis: Analysis data from backend preview
 * - user: Current user object
 * - oldAccountType: Current account type
 * - newAccountType: Target account type
 * - onMigrationComplete: Callback when migration is successful
 */
const MigrationSelectionModal = ({ 
  show, 
  onHide, 
  migrationAnalysis, 
  user, 
  oldAccountType, 
  newAccountType,
  onMigrationComplete 
}) => {
  const [selectedConnections, setSelectedConnections] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Auto-select recommended connections when modal opens
  useEffect(() => {
    if (migrationAnalysis && show) {
      const compatibleConnections = migrationAnalysis.compatible_connection_details || [];
      const maxSeats = migrationAnalysis.new_seat_limit;
      
      // Auto-select the highest priority connections up to seat limit
      const recommended = compatibleConnections
        .slice(0, Math.min(maxSeats, compatibleConnections.length))
        .map(conn => conn.connection_id);
      
      setSelectedConnections(recommended);
      setError('');
      setSuccess('');
    }
  }, [migrationAnalysis, show]);

  const handleConnectionToggle = (connectionId) => {
    const maxSeats = migrationAnalysis?.new_seat_limit || 1;
    
    if (selectedConnections.includes(connectionId)) {
      // Remove from selection
      setSelectedConnections(prev => prev.filter(id => id !== connectionId));
    } else {
      // Add to selection (check seat limit)
      if (selectedConnections.length >= maxSeats) {
        setError(`You can only select ${maxSeats} connection(s) for the ${newAccountType} plan.`);
        setTimeout(() => setError(''), 3000);
        return;
      }
      setSelectedConnections(prev => [...prev, connectionId]);
    }
  };

  const handleConfirmMigration = async () => {
    try {
      setProcessing(true);
      setError('');
      
      if (selectedConnections.length === 0) {
        setError('Please select at least one connection to keep.');
        setProcessing(false);
        return;
      }

      // Execute selective migration
      const response = await apiClient.post('/api/connections/select-connections', {
        user_id: user.user_id,
        old_account_type: oldAccountType,
        new_account_type: newAccountType,
        selected_connections: selectedConnections
      });

      if (response.data && response.data.success) {
        const result = response.data;
        setSuccess(
          `Migration completed successfully! ${result.migrated_count} connection(s) migrated, ` +
          `${result.suspended_count} connection(s) suspended.`
        );
        
        // Wait a moment to show success, then complete
        setTimeout(() => {
          onMigrationComplete(result);
          onHide();
        }, 2000);
      } else {
        setError(response.data?.message || 'Migration failed. Please try again.');
      }
    } catch (err) {
      console.error('Migration selection failed:', err);
      setError(
        err.response?.data?.message || 
        'Migration failed. Please contact support if the issue persists.'
      );
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Unknown';
    }
  };

  const getConnectionIcon = (connectionType) => {
    switch (connectionType?.toLowerCase()) {
      case 'brokerage':
      case 'large_brokerage':
      case 'medium_brokerage':
      case 'small_brokerage':
        return <FaBuilding className="text-primary" />;
      case 'team':
      case 'large_team':
      case 'medium_team':
      case 'small_team':
        return <FaUsers className="text-success" />;
      default:
        return <FaUsers className="text-info" />;
    }
  };

  const getPriorityBadge = (index, total) => {
    if (index < total * 0.3) return <Badge bg="success">High Priority</Badge>;
    if (index < total * 0.7) return <Badge bg="warning">Medium Priority</Badge>;
    return <Badge bg="secondary">Low Priority</Badge>;
  };

  if (!migrationAnalysis) {
    return null;
  }

  const compatibleConnections = migrationAnalysis.compatible_connection_details || [];
  const incompatibleConnections = migrationAnalysis.incompatible_connection_details || [];
  const maxSeats = migrationAnalysis.new_seat_limit;
  const currentlySelected = selectedConnections.length;

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="xl" 
      centered 
      backdrop="static"
      style={{ zIndex: 2200 }}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <FaExclamationTriangle className="text-warning me-2" />
          Select Connections to Keep
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert variant="success" className="mb-3">
            <FaCheckCircle className="me-2" />
            {success}
          </Alert>
        )}

        <Alert variant="warning" className="mb-4">
          <h6><FaExclamationTriangle className="me-2" />Account Downgrade Impact</h6>
          <p className="mb-2">
            <strong>Changing from {oldAccountType} to {newAccountType}</strong>
          </p>
          <ul className="mb-0">
            <li>Current connections: <strong>{migrationAnalysis.current_connections}</strong></li>
            <li>New seat limit: <strong>{maxSeats}</strong></li>
            <li>Connections to lose: <strong>{migrationAnalysis.connections_to_lose}</strong></li>
            <li>Compatible connections: <strong>{compatibleConnections.length}</strong></li>
            {incompatibleConnections.length > 0 && (
              <li className="text-danger">
                Incompatible connections: <strong>{incompatibleConnections.length}</strong> (will be suspended)
              </li>
            )}
          </ul>
        </Alert>

        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6>Select {maxSeats} connection(s) to keep:</h6>
            <div>
              <Badge bg={currentlySelected === maxSeats ? 'success' : 'primary'}>
                {currentlySelected} / {maxSeats} selected
              </Badge>
            </div>
          </div>
          
          {currentlySelected < maxSeats && (
            <Alert variant="info" className="small">
              <strong>Recommendation:</strong> We've pre-selected your most valuable connections. 
              You can modify the selection below.
            </Alert>
          )}
        </div>

        {compatibleConnections.length > 0 ? (
          <Card className="mb-3">
            <Card.Header>
              <h6 className="mb-0">
                <FaCheckCircle className="text-success me-2" />
                Compatible Connections ({compatibleConnections.length})
              </h6>
            </Card.Header>
            <Card.Body className="p-0">
              <Table striped hover responsive className="mb-0">
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}>Select</th>
                    <th>Connection</th>
                    <th>Type</th>
                    <th>Company</th>
                    <th>Created</th>
                    <th>Priority</th>
                  </tr>
                </thead>
                <tbody>
                  {compatibleConnections.map((connection, index) => (
                    <tr 
                      key={connection.connection_id}
                      className={selectedConnections.includes(connection.connection_id) ? 'table-success' : ''}
                    >
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={selectedConnections.includes(connection.connection_id)}
                          onChange={() => handleConnectionToggle(connection.connection_id)}
                          disabled={
                            !selectedConnections.includes(connection.connection_id) && 
                            currentlySelected >= maxSeats
                          }
                        />
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          {getConnectionIcon(connection.connection_type)}
                          <div className="ms-2">
                            <div className="fw-bold">
                              {connection.connection_name || `Connection ${index + 1}`}
                            </div>
                            <div className="text-muted small">
                              ID: {connection.connection_id.substring(0, 8)}...
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <Badge bg="info" className="text-capitalize">
                          {connection.connection_type || 'Unknown'}
                        </Badge>
                      </td>
                      <td>
                        {connection.company_name || 'No company'}
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <FaClock className="text-muted me-1" />
                          {formatDate(connection.created_date)}
                        </div>
                      </td>
                      <td>
                        {getPriorityBadge(index, compatibleConnections.length)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        ) : (
          <Alert variant="danger">
            <FaTimes className="me-2" />
            No compatible connections found for the {newAccountType} plan.
          </Alert>
        )}

        {incompatibleConnections.length > 0 && (
          <Card className="border-warning">
            <Card.Header className="bg-warning bg-opacity-10">
              <h6 className="mb-0">
                <FaTimes className="text-warning me-2" />
                Incompatible Connections ({incompatibleConnections.length})
              </h6>
              <small className="text-muted">
                These connections will be suspended (can be reactivated by upgrading)
              </small>
            </Card.Header>
            <Card.Body className="p-0">
              <Table responsive className="mb-0">
                <thead>
                  <tr>
                    <th>Connection</th>
                    <th>Type</th>
                    <th>Company</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {incompatibleConnections.map((connection, index) => (
                    <tr key={connection.connection_id} className="table-warning">
                      <td>
                        <div className="d-flex align-items-center">
                          {getConnectionIcon(connection.connection_type)}
                          <div className="ms-2">
                            <div className="fw-bold">
                              {connection.connection_name || `Connection ${index + 1}`}
                            </div>
                            <div className="text-muted small">
                              ID: {connection.connection_id.substring(0, 8)}...
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <Badge bg="warning" className="text-capitalize">
                          {connection.connection_type || 'Unknown'}
                        </Badge>
                      </td>
                      <td>
                        {connection.company_name || 'No company'}
                      </td>
                      <td>
                        <Badge bg="warning">
                          Will be suspended
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        )}
      </Modal.Body>
      
      <Modal.Footer>
        <Button 
          variant="secondary" 
          onClick={onHide}
          disabled={processing}
        >
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleConfirmMigration}
          disabled={processing || currentlySelected === 0}
        >
          {processing ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Processing Migration...
            </>
          ) : (
            `Confirm Migration (Keep ${currentlySelected} Connection${currentlySelected !== 1 ? 's' : ''})`
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MigrationSelectionModal;